import { useState } from "react";

import { MRTGrid } from "@foodpilot/foods";
import { User } from "src/api/client-api/user/useAllUsers";
import { useUserListContext } from "./UserListContext";
import { useTranslation } from "react-i18next";
import {
    Alert,
    Box,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useDeleteUser } from "src/api/client-api/user/useDeleteUser";
import { useSnackbar } from "src/utils/useSnackbar";
import { useQueryClient } from "react-query";
import { UserModal } from "./UserModal";
import { UserService } from "src/services/UserService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { resolveUserManagementPermissions } from "src/pages/Settings/Users/userManagementPermissions";
import { UserOrganizationRolesModal } from "src/pages/Settings/Users/UserOrganizationRolesModal";
import { canManagePlatformAdminTarget, isPlatformAdmin } from "src/pages/Settings/Users/userModalRoles";

export const UserGrid = () => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const { canUser, isAdmin } = useFoodpilotContext();
    const queryClient = useQueryClient();

    const { users, isLoading, mrt_columns, totalItems, userPage, setUserPage, sorting, setSorting, delayedSearchUser } =
        useUserListContext();

    // Modal and delete dialog states
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const deleteUserMutation = useDeleteUser();
    const actorIsPlatformAdmin = isAdmin();
    const { canModifyUsers, canDeleteUsers, canAssignUserRoles, canImpersonateUsers, canShowRowActions } =
        resolveUserManagementPermissions(canUser, isAdmin);

    // Actions
    const handleEditClick = (user: User) => {
        setEditingUser(user);
        setShowEditModal(true);
    };

    const handleDeleteClick = (user: User) => {
        if (!canManagePlatformAdminTarget(actorIsPlatformAdmin, user.roles)) {
            return;
        }

        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete || !canManagePlatformAdminTarget(actorIsPlatformAdmin, userToDelete.roles)) {
            return;
        }

        try {
            await deleteUserMutation.mutateAsync(userToDelete.id);
            setSnackbarProps({
                message: t("users.delete.success", "User deleted successfully"),
                type: "success",
            });
            queryClient.invalidateQueries("allUsers");
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        } catch {
            setSnackbarProps({
                message: t("users.delete.error", "Failed to delete user"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleImpersonateClick = (email: string) => {
        UserService.impersonateUser(email);
    };

    return (
        <>
            <MRTGrid
                data={users || []}
                columns={mrt_columns.mrt_columns}
                enableRowActions={canShowRowActions}
                enableRowSelection={false}
                enablePagination={true}
                enableSelectAll={false}
                enableColumnPinning={true}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        size: 120,
                        grow: false,
                    },
                }}
                renderRowActions={({ row }) => {
                    const canManageTarget = canManagePlatformAdminTarget(actorIsPlatformAdmin, row.original.roles);

                    return (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {canImpersonateUsers && (row.original.canImpersonate ?? actorIsPlatformAdmin) && (
                                <Box
                                    component="button"
                                    onClick={() => handleImpersonateClick(row.original.email)}
                                    aria-label={`${t("users.impersonate", "Impersonate")} ${row.original.email}`}
                                    sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s",
                                        "&:hover": {
                                            backgroundColor: "#F5F5F5",
                                        },
                                    }}
                                >
                                    <FoodsIcon icon="userAccount" size={1.75} />
                                </Box>
                            )}
                            {(canModifyUsers || (canAssignUserRoles && canManageTarget)) && (
                                <Box
                                    component="button"
                                    onClick={() => handleEditClick(row.original)}
                                    aria-label={`${
                                        canModifyUsers ?
                                            t("users.editUser", "Edit")
                                        :   t("users.organizationRoles", "Companies & Roles")
                                    } ${row.original.email}`}
                                    sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s",
                                        "&:hover": {
                                            backgroundColor: "#F5F5F5",
                                        },
                                    }}
                                >
                                    <FoodsIcon icon="edit" size={1} />
                                </Box>
                            )}
                            {canDeleteUsers && canManageTarget && (
                                <Box
                                    component="button"
                                    onClick={() => handleDeleteClick(row.original)}
                                    aria-label={`${t("users.deleteUser", "Delete")} ${row.original.email}`}
                                    sx={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s",
                                        "&:hover": {
                                            backgroundColor: "#F5F5F5",
                                        },
                                    }}
                                >
                                    <FoodsIcon icon="delete" size={1} />
                                </Box>
                            )}
                        </Stack>
                    );
                }}
                state={{
                    isLoading: isLoading,
                    pagination: userPage,
                    sorting: sorting,
                    globalFilter: delayedSearchUser,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    columnSizing: mrt_columns.columnSizing,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                // Server-side pagination
                rowCount={totalItems}
                manualPagination={true}
                onPaginationChange={setUserPage}
                manualSorting={true}
                onSortingChange={setSorting}
                manualFiltering={true}
                localization={{
                    actions: "",
                }}
                muiTableBodyRowProps={{
                    sx: {
                        "&:hover": {
                            backgroundColor: "#F9F9F8",
                        },
                        "& .MuiTableCell-root": {
                            paddingTop: "16px",
                            paddingBottom: "16px",
                        },
                    },
                }}
            />

            {canModifyUsers ?
                <UserModal
                    open={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                    }}
                    editingUser={editingUser || undefined}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                        queryClient.invalidateQueries("allUsers");
                    }}
                />
            : canAssignUserRoles ?
                <UserOrganizationRolesModal
                    open={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                    }}
                    user={editingUser}
                />
            :   null}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                aria-labelledby="delete-user-title"
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        padding: "8px",
                    },
                }}
            >
                <DialogTitle
                    id="delete-user-title"
                    sx={{ fontWeight: 700, fontSize: "1.125rem", color: "#1E1E1E", padding: "24px 24px 16px" }}
                >
                    {t("users.delete.title", "Delete User")}
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: "0 24px 24px",
                    }}
                >
                    <Typography sx={{ fontSize: "0.875rem", color: "#50504D", lineHeight: "1.6" }}>
                        {t("users.delete.message", "Are you sure you want to delete this user?")}
                        {userToDelete && (
                            <strong>
                                {" "}
                                {userToDelete.firstname} {userToDelete.lastname}
                            </strong>
                        )}
                    </Typography>
                    {isPlatformAdmin(userToDelete?.roles) && (
                        <Alert severity="warning" role="alert" sx={{ marginTop: (theme) => theme.spacing(2) }}>
                            {t(
                                "users.delete.platformAdminWarning",
                                "This user is a platform administrator. Deleting this account may remove critical platform access.",
                            )}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "16px 24px 24px",
                        gap: "12px",
                    }}
                >
                    <Button
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            borderColor: "#E7E6E4",
                            color: "#393A36",
                            "&:hover": {
                                borderColor: "#D0D0D0",
                                backgroundColor: "#F5F5F5",
                            },
                        }}
                    >
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={deleteUserMutation.isLoading}
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            backgroundColor: "#D32F2F",
                            "&:hover": {
                                backgroundColor: "#B71C1C",
                            },
                        }}
                    >
                        {deleteUserMutation.isLoading ?
                            <CircularProgress size={16} color="inherit" />
                        :   t("common.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
